import {
  Autosuggest,
  AutosuggestProps,
  Button,
  Grid,
  NonCancelableCustomEvent,
} from '@amzn/awsui-components-core';
import {
  ContentType,
  PublicationSearchResultItem,
  TechDomain,
  TechDomainDisplayNames,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { autoSuggestionTitleWordLimit } from '../../api/UtilsApi';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import RouterPaths from '../../common/RouterPaths';
import '../../resources/styles/scss/components/search/SearchBar.css';
import BrowserHistory from '../utils/BrowserHistory';
import { autoSuggestionContentTypeDisplayNames } from '../utils/DisplayNames';
import { decodeSearchtextString } from '../utils/Search';

interface AutosuggestOptionWithContent extends AutosuggestProps.Option {
  contentId: string;
  contentType: ContentType;
  description: string;
  publicationWorkflowId: number;
  url: string;
}

interface AutosuggestGroupWithContent {
  label: string;
  options: AutosuggestOptionWithContent[];
}

interface State {
  input: string;
  suggestions: Array<AutosuggestProps.Option | AutosuggestProps.OptionGroup>;
  statusType: AutosuggestProps.StatusType;
}

class SearchBar extends React.Component<{}, State> {
  private mplServiceClient: IMplServiceClient;
  // TODO: After we are able to fetch top search terms dynamically, we need to generate the default suggestions in a programmable way.
  private defaultSuggestions = [
    { value: constants.TOP1_SEARCH_TERM },
    { value: constants.TOP2_SEARCH_TERM },
    { value: constants.TOP3_SEARCH_TERM },
    { value: constants.TOP4_SEARCH_TERM },
    { value: constants.TOP5_SEARCH_TERM },
  ];
  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.state = {
      input: '',
      statusType: 'pending',
      suggestions: this.defaultSuggestions,
    };
  }
  public render() {
    const { input } = this.state;
    return (
      <div className="searchbar-container">
        <Grid disableGutters gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <div className="search-box">
            <Autosuggest
              value={input}
              options={this.state.suggestions}
              onChange={({ detail }) =>
                this.setState({
                  input: detail.value,
                })
              }
              onSelect={this.handleSelectOption}
              placeholder={constants.SEARCH_LABEL}
              onLoadItems={({ detail }) => this.generateSuggestions(detail.filteringText)}
              empty={constants.SEARCH_BAR_EMPTY}
              filteringType="manual"
              statusType={this.state.statusType}
              loadingText={constants.LOADING_AUTO_SUGGESTIONS}
              enteredTextLabel={(value) => `Use: "${value}"`}
              ariaRequired
            />
          </div>
          <div className="search-button">
            <Button
              iconName="search"
              variant="normal"
              ariaLabel="Search"
              onClick={this.handleSearchClick}
            />
          </div>
        </Grid>
      </div>
    );
  }

  private handleSearchClick = () => {
    const { input } = this.state;
    if (input) {
      BrowserHistory.push(RouterPaths.HOME_PAGE, { searchInput: encodeURIComponent(input) });
    }
  };

  private handleSelectOption = (event: NonCancelableCustomEvent<AutosuggestProps.SelectDetail>) => {
    const selectedOption = event.detail.selectedOption as AutosuggestOptionWithContent;
    const selectedValue = selectedOption?.value ?? event.detail.value; // selected option is just a keyword

    // If user selects one auto suggestion, process it
    if (selectedOption?.contentId) {
      // If the auto suggestion is an actual publication, open up the content
      if (selectedOption.contentType === ContentType.ExternalResource) {
        window.open(selectedOption.url);
      } else {
        BrowserHistory.push(
          getContentPath({
            contentId: selectedOption.contentId,
            mode: 'publication',
            workflowId: selectedOption.publicationWorkflowId,
          }),
        );
      }
    } else if (selectedValue) {
      // If the auto suggestion is a key word, trigger search page
      BrowserHistory.push(RouterPaths.HOME_PAGE, {
        searchInput: encodeURIComponent(selectedValue),
      });
    }
    // If user clicks somewhere else on the page, do nothing
  };

  private generateSuggestions = async (input: string) => {
    if (!input) {
      this.setState({
        suggestions: this.defaultSuggestions,
      });
      return;
    }
    this.setState({
      statusType: 'loading',
    });
    const suggestions: Array<AutosuggestProps.Option | AutosuggestProps.OptionGroup> =
      this.defaultSuggestions.filter((suggestion) =>
        suggestion.value.toLowerCase().includes(input.toLowerCase()),
      ); // manually filter default search terms
    const [strategySuggestions, guideSuggestions, patternSuggestions] = await Promise.all([
      this.generateSuggestionsForType(input, ContentType.Strategy),
      this.generateSuggestionsForType(input, ContentType.Guide),
      this.generateSuggestionsForType(input, ContentType.Pattern),
    ]);
    if (strategySuggestions) {
      suggestions.push(strategySuggestions);
    }
    if (guideSuggestions) {
      suggestions.push(guideSuggestions);
    }
    if (patternSuggestions) {
      suggestions.push(patternSuggestions);
    }
    this.setState({
      statusType: 'finished',
      suggestions,
    });
  };

  private generateSuggestionsForType = async (input: string, contentType: ContentType) => {
    const { publications, totalFound } =
      await this.mplServiceClient.searchPublicationSuggestionsByType(
        decodeSearchtextString(input),
        contentType,
        true,
      );
    const contentTypeLabel = autoSuggestionContentTypeDisplayNames.get(contentType);
    return this.generateSubSuggestions(publications, contentTypeLabel ?? '', totalFound);
  };

  private generateDescription = (publication: PublicationSearchResultItem): string => {
    const technologies: TechDomain[] = [];
    if (publication.mainTechnology) {
      technologies.push(publication.mainTechnology);
    }
    if (publication.technologies) {
      publication.technologies.forEach((technology) => technologies.push(technology));
    }
    if (!technologies.length) {
      return '';
    }
    let description: string = '';
    const techCount = technologies.length;
    if (techCount === 1) {
      // @ts-expect-error TS2345 - strict migration
      description = 'Technology: '.concat(TechDomainDisplayNames.get(technologies[0]));
    } else if (techCount === 2) {
      description = 'Technologies: '
        // @ts-expect-error TS2345 - strict migration
        .concat(TechDomainDisplayNames.get(technologies[0]) + ', ')
        // @ts-expect-error TS2345 - strict migration
        .concat(TechDomainDisplayNames.get(technologies[1]));
    } else if (techCount > 2) {
      description = 'Technologies: '
        // @ts-expect-error TS2345 - strict migration
        .concat(TechDomainDisplayNames.get(technologies[0]) + ', ')
        // @ts-expect-error TS2345 - strict migration
        .concat(TechDomainDisplayNames.get(technologies[1]) + ', & ')
        .concat(`${technologies.length - 2} more`);
    }
    return description;
  };

  private generateTitle = (title: string): string => {
    const titleArr = title.split(' ');
    if (titleArr.length > autoSuggestionTitleWordLimit) {
      return titleArr
        .slice(0, autoSuggestionTitleWordLimit + 1)
        .join(' ')
        .concat(' ...');
    } else {
      return title;
    }
  };

  private generateSubSuggestions = (
    publications: PublicationSearchResultItem[],
    contentTypeLabel: string,
    total: number,
  ): AutosuggestGroupWithContent | null => {
    if (Array.isArray(publications) && publications.length > 0) {
      const subSuggestions: AutosuggestOptionWithContent[] = publications.map((publication) => {
        const description = this.generateDescription(publication);
        const option: AutosuggestOptionWithContent = {
          contentId: publication.contentId,
          contentType: publication.contentType,
          description,
          publicationWorkflowId: publication.publicationWorkflowId,
          url: publication.url,
          value: this.generateTitle(publication.contentName),
        };
        return option;
      });
      return {
        label: `${contentTypeLabel} (${total})`,
        options: subSuggestions,
      };
    } else {
      return null;
    }
  };
}

export default SearchBar;
